<script>
export default {
  props: {
    active: { type: Boolean, default: false },
    fields: { type: Array, default: () => [] },
    initialAnswers: { type: Array, default: () => [] },
    dismissible: { type: Boolean, default: true },
    submitAction: { type: String, required: true },
    submitPayload: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      isLoading: false,
      answers: this.buildInitialAnswers(),
      errors: {},
    };
  },
  computed: {
    hasRequired() {
      return this.fields.some((f) => f.required);
    },
    canDismiss() {
      return this.dismissible && !this.hasRequired;
    },
    hasAnyInput() {
      return this.fields.some((f) => {
        const raw = this.answers[f.name];
        if (f.type === 'checkbox') return raw === true;
        return raw !== '' && raw !== null && raw !== undefined;
      });
    },
  },
  watch: {
    fields() {
      this.answers = this.buildInitialAnswers();
    },
  },
  methods: {
    buildInitialAnswers() {
      const existing = (this.initialAnswers || []).reduce((acc, a) => {
        acc[a.name] = a.value;
        return acc;
      }, {});

      return (this.fields || []).reduce((acc, field) => {
        if (Object.prototype.hasOwnProperty.call(existing, field.name)) {
          acc[field.name] = field.type === 'checkbox'
            ? existing[field.name] === 'true' || existing[field.name] === true
            : existing[field.name];
        } else {
          acc[field.name] = field.type === 'checkbox' ? false : '';
        }
        return acc;
      }, {});
    },
    onClose() {
      if (!this.canDismiss) return;
      this.$emit('close');
    },
    onDismiss() {
      if (!this.canDismiss) return;
      // `dismiss` lets the parent persist the choice; `close` is the hide fallback
      // for parents that do not handle persistence (e.g. bundles).
      this.$emit('dismiss');
      this.$emit('close');
    },
    formatAnswer(field, raw) {
      if (field.type === 'checkbox') {
        return { value: raw ? 'true' : 'false', label: raw ? this.$t('yes') : this.$t('no') };
      }
      if (field.type === 'select') {
        return { value: raw, label: String(raw ?? '').slice(0, 255) };
      }
      // Free text: backend keeps the question label, so this is only for the
      // request shape — truncate to respect the answer.label max length.
      return { value: String(raw ?? ''), label: String(raw ?? '').slice(0, 255) };
    },
    isAnswerEmpty(field, raw) {
      if (field.type === 'checkbox') return false;
      return raw === '' || raw === null || raw === undefined;
    },
    // A required field needs a real value; a required checkbox must be checked.
    isAnswerMissing(field, raw) {
      if (field.type === 'checkbox') return raw !== true;
      return this.isAnswerEmpty(field, raw);
    },
    validateField(field) {
      const missing = field.required && this.isAnswerMissing(field, this.answers[field.name]);
      this.$set(this.errors, field.name, missing ? this.$t('field_required') : '');
    },
    submit() {
      const payload = [];
      let hasError = false;

      for (const field of this.fields) {
        const raw = this.answers[field.name];
        if (!(field.required && this.isAnswerMissing(field, raw))) {
          this.$set(this.errors, field.name, '');
          if (!this.isAnswerEmpty(field, raw)) {
            payload.push({
              name: field.name,
              answer: this.formatAnswer(field, raw),
            });
          }
        } else {
          this.$set(this.errors, field.name, this.$t('field_required'));
          hasError = true;
        }
      }

      if (hasError) return;

      this.isLoading = true;
      this.$store
        .dispatch(this.submitAction, { ...this.submitPayload, answers: payload })
        .then(() => {
          this.$showMessage.success();
          this.$emit('submitted', payload);
        })
        .catch(() => {
          this.$showMessage.error(this.$t('save_error'));
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
  i18n: {
    messages: {
      fr: {
        title: 'Avant de commencer',
        intro: 'Merci de renseigner les informations suivantes.',
        field_required: 'Ce champ est obligatoire.',
        select_placeholder: '-- Sélectionner --',
        dismiss: 'Je ne souhaite pas répondre',
        save: 'Enregistrer',
        save_error: "Impossible d'enregistrer vos réponses, veuillez réessayer.",
        yes: 'Oui',
        no: 'Non',
      },
      en: {
        title: 'Before you start',
        intro: 'Please fill in the following information.',
        field_required: 'This field is required.',
        select_placeholder: '-- Select --',
        dismiss: 'I prefer not to answer',
        save: 'Save',
        save_error: 'Unable to save your answers, please try again.',
        yes: 'Yes',
        no: 'No',
      },
    },
  },
};
</script>

<template>
  <b-modal
    :active="active"
    :can-cancel="false"
    has-modal-card
    @close="onClose"
  >
    <form class="modal-card" @submit.prevent="submit">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ $t('title') }}
        </p>
      </header>
      <section class="modal-card-body has-text-left">
        <p class="mb-4">
          {{ $t('intro') }}
        </p>

        <b-field
          v-for="field in fields"
          :key="field.name"
          :type="{ 'is-danger': errors[field.name] }"
          :message="errors[field.name]"
        >
          <template v-if="field.type !== 'checkbox'" #label>
            {{ field.label }}
            <span v-if="field.required" class="has-text-danger">*</span>
          </template>

          <b-input
            v-if="field.type === 'text'"
            v-model="answers[field.name]"
            :required="field.required"
            maxlength="100"
            :has-counter="false"
            expanded
            @blur="validateField(field)"
          />

          <b-input
            v-else-if="field.type === 'textarea'"
            v-model="answers[field.name]"
            maxlength="1000"
            :has-counter="false"
            type="textarea"
            :required="field.required"
            expanded
            @blur="validateField(field)"
          />

          <b-select
            v-else-if="field.type === 'select'"
            v-model="answers[field.name]"
            :required="field.required"
            expanded
            @input="validateField(field)"
          >
            <option value="" disabled>
              {{ $t('select_placeholder') }}
            </option>
            <option v-for="opt in field.options || []" :key="opt" :value="opt">
              {{ opt }}
            </option>
          </b-select>

          <b-checkbox
            v-else-if="field.type === 'checkbox'"
            v-model="answers[field.name]"
            @input="validateField(field)"
          >
            {{ field.label }}
            <span v-if="field.required" class="has-text-danger">*</span>
          </b-checkbox>
        </b-field>
      </section>
      <footer class="modal-card-foot is-justify-content-flex-end">
        <b-button
          v-if="canDismiss"
          type="is-text"
          @click="onDismiss"
        >
          {{ $t('dismiss') }}
        </b-button>
        <b-button
          type="is-primary is-custom"
          native-type="submit"
          :loading="isLoading"
          :disabled="!hasAnyInput && !hasRequired"
        >
          {{ $t('save') }}
        </b-button>
      </footer>
    </form>
  </b-modal>
</template>
